import { useMutation, useQueryClient } from "@tanstack/react-query";

import { catchError } from "shared/utils/catch-error";

import {
  CertificateMetadata,
  cloudStorageKeys,
  EncryptedCertificate,
} from "../types";
import { getCloudStorage } from "../utils/get-cloud-storage";
import { getCertificatesQueryOptions } from "./use-certificates-query";

const chunkSize = 4000;

function splitCertificateIntoChunks(certificate: EncryptedCertificate) {
  const certString = JSON.stringify(certificate);
  const chunks: string[] = [];

  for (let i = 0; i < certString.length; i += chunkSize) {
    chunks.push(certString.slice(i, i + chunkSize));
  }

  return { chunks, totalChunks: chunks.length };
}

type Params = {
  url: string;
  certificateId: string;
  standard: CertificateMetadata["standard"];
};

export const useStoreCertificateMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ url, certificateId, standard }: Params) => {
      const u = new URL(url);
      const response = await fetch(u.toString());
      const encryptedCertificate: EncryptedCertificate = await response.json();

      const { chunks, totalChunks } =
        splitCertificateIntoChunks(encryptedCertificate);

      const metadata: CertificateMetadata = {
        totalChunks,
        timestamp: Date.now(),
        holderCommitment: encryptedCertificate.holderCommitment,
        certificateId,
        standard,
      };

      const cloudStorage = getCloudStorage();

      const stringifiedCerts = await cloudStorage.getItem(
        cloudStorageKeys.certificates
      );
      const certs: CertificateMetadata[] = stringifiedCerts
        ? JSON.parse(stringifiedCerts)
        : [];

      const existingIndex = certs.findIndex(
        (m) => m.certificateId === metadata.certificateId
      );
      if (existingIndex >= 0) {
        certs[existingIndex] = metadata;
      } else {
        certs.push(metadata);
      }

      await cloudStorage.setItem(
        cloudStorageKeys.certificates,
        JSON.stringify(certs)
      );

      const promises = chunks.map((ch, i) =>
        cloudStorage.setItem(cloudStorageKeys.certificate(certificateId, i), ch)
      );

      await Promise.all(promises);

      return certs;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: getCertificatesQueryOptions({}).queryKey,
      });
    },
    onError: catchError,
  });
};
